/**
 * useAbortableEffect — variante de `useEffect` que provee un
 * `AbortSignal` vivo mientras el effect está activo y lo aborta en
 * el cleanup. Permite cancelar fetches en vuelo cuando el componente
 * se desmonta o las dependencias cambian.
 *
 * Uso típico en una página con fetch:
 *
 * ```tsx
 * useAbortableEffect(
 *   (signal) => {
 *     companyService.getAll(params, { signal })
 *       .then(setCompanies)
 *       .catch((err) => {
 *         // AbortError al desmontar — ignorar, es intencional
 *         if (signal.aborted) return;
 *         toast.error(err.message);
 *       });
 *   },
 *   [params],
 * );
 * ```
 *
 * El hook se encarga de:
 *   - Crear un `AbortController` nuevo en cada run del effect
 *   - Abortarlo en cleanup (cambio de deps o unmount)
 *   - Soportar cleanup retornado por el callback (además del abort)
 */

import { useEffect } from 'react';
import type { DependencyList } from 'react';

type Cleanup = (() => void) | void;

export function useAbortableEffect(
  effect: (signal: AbortSignal) => Cleanup | Promise<Cleanup>,
  deps?: DependencyList,
): void {
  useEffect(() => {
    const controller = new AbortController();
    let userCleanup: Cleanup = undefined;

    const result = effect(controller.signal);

    // Si el callback devuelve una Promise (es async), esperamos su
    // resolución para capturar un posible cleanup. Nota: si el effect
    // tarda en setup, el abort sigue funcionando porque ya tenemos el
    // controller listo.
    if (result instanceof Promise) {
      result.then((cleanup) => {
        userCleanup = cleanup;
      });
    } else {
      userCleanup = result;
    }

    return () => {
      controller.abort();
      if (typeof userCleanup === 'function') userCleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Detecta si el error vino de un AbortController (ya sea via
 * `fetch(..., { signal })` o programáticamente). Útil en los catch
 * de las páginas para suprimir toasts de error cuando el usuario
 * simplemente navegó a otra pestaña.
 */
export function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (err instanceof Error && err.name === 'AbortError') return true;
  return false;
}
